import { LOCALES, localeCodeSchema } from './i18n.js';
import { z } from 'zod';
import { label } from './text-policy.js';
/** Postal address, all parts optional — a floor has no street address of its own. */
export const locationAddressSchema = z.object({
    line1: label({ max: 200, allowEmpty: true }).optional(),
    line2: label({ max: 200, allowEmpty: true }).optional(),
    city: label({ max: 120, allowEmpty: true }).optional(),
    region: label({ max: 120, allowEmpty: true }).optional(),
    postalCode: label({ max: 40, allowEmpty: true }).optional(),
    /** ISO 3166-1 alpha-2, uppercase. */
    country: z.string().trim().length(2).toUpperCase().optional(),
});
/**
 * The OUTBOUND / stored reading of the same address: the pre-handoff-0009 rules (trim + max),
 * and NOT the text policy. `locationAddressSchema` is write-path only — an address stored
 * before the policy, or by a seed/migration that never went through it, must still read back
 * as stored rather than collapsing to `{}` (handoff 0009 §1a rule 12, "never validate on read").
 * Same inferred type as `locationAddressSchema`.
 */
export const storedLocationAddressSchema = z.object({
    line1: z.string().trim().max(200).optional(),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(120).optional(),
    region: z.string().trim().max(120).optional(),
    postalCode: z.string().trim().max(40).optional(),
    country: z.string().trim().length(2).toUpperCase().optional(),
});
/**
 * An IANA zone name, checked against the runtime's own tz database.
 *
 * The predicate is "does `Intl` accept this zone", NOT "is it in
 * `Intl.supportedValuesOf('timeZone')`". Those are different sets, and reading
 * the second as the set of legal identifiers is a bug we already shipped once:
 *
 *   - `supportedValuesOf` is an ENUMERATION for building a picker. It is
 *     canonical and de-aliased, and it omits the entire `Etc/*` group — 418
 *     zones here, none of them `Etc/*`.
 *   - `resolvedOptions().timeZone` returns a CANONICALIZED identifier, and
 *     canonicalization folds `Etc/UTC`, `Etc/GMT` and `GMT` onto the literal
 *     string `UTC` — which the enumeration does not contain.
 *
 * So a browser in London in winter resolves to `UTC`, a zone that is perfectly
 * usable and was rejected as invalid. It is not only a UTC quirk: link names
 * are absent too, and which spelling of a link is canonical (`Asia/Kolkata` vs
 * `Asia/Calcutta`) flips between ICU versions, so the enumeration is not stable
 * across the browser and the server anyway.
 *
 * `Intl.DateTimeFormat` throws `RangeError` on an unusable zone and accepts
 * every alias, which is exactly the question worth asking. We then store the
 * canonical spelling so the whole UTC family lands on one value rather than
 * four.
 */
const canonicalTimezone = (value) => {
    try {
        return new Intl.DateTimeFormat(undefined, { timeZone: value }).resolvedOptions().timeZone;
    }
    catch {
        return null;
    }
};
export const ianaTimezoneSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine((value) => canonicalTimezone(value) !== null, { message: 'INVALID_TIMEZONE' })
    .transform((value) => canonicalTimezone(value) ?? value);
/**
 * The wire shape of a location. An allow-list, deliberately narrower than the
 * row: `tenantId` is never exposed here — it comes from the verified token,
 * never from a response body (docs/008 invariant S1/S7).
 */
/**
 * The languages this site offers a visitor, and the one its kiosks open in.
 *
 * A SITE PROPERTY, NOT A CONTENT PROPERTY. What copy happens to be translated is
 * answered by the resolved flow's `supportedLocales`; this answers "what should a
 * person standing in this lobby be offered", which only whoever runs the lobby
 * knows. Once field labels are machine-translated, almost everything is authored in
 * every locale, so without this an English-only reception desk would show three
 * language chips it has no staff for.
 *
 * Non-empty, and deduplicated rather than refused on a repeat: a checkbox group
 * that submits `['en','en']` is a client bug, and failing the admin's save over it
 * teaches them nothing. `LOCALES.length` bounds it because the platform list is the
 * only source of members.
 */
export const locationLocalesSchema = z
    .array(localeCodeSchema)
    .min(1)
    .max(LOCALES.length)
    .transform((locales) => LOCALES.filter((locale) => locales.includes(locale)));
/**
 * The locale a kiosk at this site opens in, and the middle step of every fallback
 * chain resolved for it. `null` INHERITS `Tenant.defaultLocale` — a site that has
 * never been configured is not the same as one deliberately set to the tenant's
 * language, and only the first should follow a later change to the tenant default.
 */
export const locationDefaultLocaleSchema = localeCodeSchema.nullable();
export const locationDTOSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    timezone: z.string().min(1),
    /** OUTBOUND: never re-validated against the text policy (handoff 0009 §1a rule 12). */
    address: storedLocationAddressSchema,
    parentLocationId: z.string().min(1).nullable(),
    /**
     * READ SIDE IS LENIENT, DELIBERATELY. Empty means "never configured" and the
     * server resolves it to the effective default locale at read time; bounding it
     * here the way the write schemas do would fail an admin's list over exactly the
     * row they opened the page to fix.
     */
    supportedLocales: z.array(localeCodeSchema),
    defaultLocale: locationDefaultLocaleSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * A default that is not in the offered set would be picked at open and then be
 * unpickable again from the switcher — a state the visitor cannot get back out of.
 * Checked on both write schemas, and on the patch only when both halves are present:
 * a patch that changes one alone cannot be checked against the stored row from
 * inside a Zod schema, and the service completes the invariant there.
 */
const assertDefaultLocaleIsOffered = (value, ctx) => {
    if (value.supportedLocales === undefined)
        return;
    if (value.defaultLocale === undefined || value.defaultLocale === null)
        return;
    if (!value.supportedLocales.includes(value.defaultLocale)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultLocale'],
            message: 'LOCATION_DEFAULT_LOCALE_NOT_SUPPORTED',
        });
    }
};
export const createLocationSchema = z
    .object({
    name: label({ max: 120 }),
    timezone: ianaTimezoneSchema,
    address: locationAddressSchema.default({}),
    parentLocationId: z.string().min(1).nullable().default(null),
    /**
     * DEFAULTS TO EMPTY, meaning "not configured" — the server resolves that to the
     * effective default locale alone. Defaulting to every platform locale instead
     * would hand a brand-new site three language chips nobody chose.
     */
    supportedLocales: locationLocalesSchema.optional(),
    defaultLocale: locationDefaultLocaleSchema.default(null),
})
    .superRefine(assertDefaultLocaleIsOffered);
export const updateLocationSchema = createLocationSchema
    .innerType()
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'EMPTY_UPDATE' })
    .superRefine(assertDefaultLocaleIsOffered);
/**
 * The ceiling on rows returned by `GET /api/web/locations/tree` in ONE response.
 *
 * `GET /api/web/locations` (the flat, paginated list above) stays bounded by
 * `MAX_PAGE_SIZE` (100) per page forever — other consumers depend on that page
 * size, and a tenant assembling a tree client-side from one page of it could
 * never reach a location past the 100th. The tree endpoint exists to answer
 * that case directly: no pagination, but still a hard cap, because `findMany`
 * must never run unbounded (docs/015-data-access.md). 5,000 is far past any
 * tenant's real campus/building/floor count. When it bites, `isTruncated` says
 * so ON THE WIRE — the same precedent `EVACUATION_ROSTER_MAX_ROWS` set for a
 * capped, unpaginated list (`./evacuation.js`).
 */
export const LOCATION_TREE_MAX_ROWS = 5000;
/**
 * The whole tenant's location tree, as ONE object in a `DataEnvelope` — never a
 * `ListEnvelope`. `ListEnvelope.meta` is `{ page, pageSize, total }`, and this
 * response has no page and no page size; filling them in would be a lie in the
 * shape of a contract, exactly the reasoning `evacuationRosterDTOSchema` gives
 * for the same choice.
 *
 * `total` is counted over the tenant's WHOLE matching set, not `locations.length`
 * — so a caller can always tell "how many exist" from "how many came back", even
 * once the cap has truncated the list beneath it.
 */
export const locationTreeDTOSchema = z.object({
    /** Every location this tenant holds (narrowed by the caller's location scope),
     *  counted — not `locations.length`. */
    total: z.number().int().nonnegative(),
    /** `true` when `total` exceeded `LOCATION_TREE_MAX_ROWS` and `locations` is a
     *  prefix of the tree rather than all of it. The console MUST render this. */
    isTruncated: z.boolean(),
    locations: z.array(locationDTOSchema),
});
//# sourceMappingURL=location.js.map